<template>
  <div class="checkout-form">
    <!-- 步骤进度条 -->
    <div class="step-progress" v-if="showSteps">
      <div 
        class="step" 
        :class="{ active: currentStep >= 1, completed: currentStep > 1, clickable: currentStep > 1 }"
        @click="goToStep(1)"
      >
        <div class="step-circle">{{ currentStep > 1 ? '' : '1' }}</div>
        <span class="step-label">确认订单</span>
      </div>
      <div class="step-line" :class="{ active: currentStep > 1 }"></div>
      <div 
        class="step" 
        :class="{ active: currentStep >= 2, completed: currentStep > 2, clickable: currentStep > 2 }"
        @click="goToStep(2)"
      >
        <div class="step-circle">{{ currentStep > 2 ? '' : '2' }}</div>
        <span class="step-label">支付</span>
      </div>
      <div class="step-line" :class="{ active: currentStep > 2 }"></div>
      <div 
        class="step" 
        :class="{ active: currentStep >= 3, completed: currentStep > 3 }"
      >
        <div class="step-circle">{{ currentStep > 3 ? '' : '3' }}</div>
        <span class="step-label">完成</span>
      </div>
    </div>
    
    <!-- 步骤1: 确认订单 -->
    <div v-if="currentStep === 1" class="step-content">
      <div class="address-section">
        <div class="section-title">
          <span class="title-icon" aria-hidden="true"></span>
          <h3>收货地址</h3>
          <button type="button" class="btn btn-outline btn-sm" @click="showAddressSelector = true">
            {{ selectedAddress ? '更换' : '选择地址' }}
          </button>
        </div>
        
        <div v-if="selectedAddress" class="address-card">
          <div class="address-header">
            <span class="receiver-name">{{ selectedAddress.receiverName }}</span>
            <span class="phone">{{ selectedAddress.phone }}</span>
            <span v-if="selectedAddress.isDefault" class="default-tag">默认</span>
          </div>
          <p class="address-detail">
            {{ selectedAddress.province }} {{ selectedAddress.city }} {{ selectedAddress.district }} {{ selectedAddress.detailAddress }}
          </p>
        </div>
        <div v-else class="no-address">
          <p>暂无收货地址，请先添加</p>
          <router-link to="/addresses" class="btn btn-primary btn-sm" @click="$emit('close')">去添加</router-link>
        </div>
      </div>

      <div class="products-section">
        <div class="section-title">
          <span class="title-icon" aria-hidden="true"></span>
          <h3>商品清单</h3>
        </div>
        <div class="order-products">
          <div v-for="item in orderItems" :key="item.productId || item.id" class="order-product">
            <img :src="getProductImageUrl(item)" :alt="item.productName" class="product-img">
            <div class="product-info">
              <p class="product-name">{{ item.productName }}</p>
              <p v-if="item.sizeName" class="product-size">尺码: {{ item.sizeName }}<span v-if="item.sizeExtraPrice > 0"> (+¥{{ item.sizeExtraPrice.toFixed(2) }})</span></p>
              <p class="product-price">¥{{ item.price.toFixed(2) }}</p>
            </div>
            <div class="product-qty">×{{ item.quantity }}</div>
            <div class="product-total">¥{{ ((item.price + (item.sizeExtraPrice || 0)) * item.quantity).toFixed(2) }}</div>
          </div>
        </div>
      </div>

      <div class="amount-section">
        <div class="amount-row">
          <span>商品金额</span>
          <span>¥{{ productAmount.toFixed(2) }}</span>
        </div>
        <div class="amount-row" v-if="sizeExtraAmount > 0">
          <span>尺寸附加值</span>
          <span>¥{{ sizeExtraAmount.toFixed(2) }}</span>
        </div>
        <div class="amount-row">
          <span>运费</span>
          <span>¥0.00</span>
        </div>
        <div class="amount-row total">
          <span>实付金额</span>
          <span>¥{{ totalAmount.toFixed(2) }}</span>
        </div>
      </div>

      <div class="notes-section">
        <label for="order-notes">订单备注</label>
        <textarea id="order-notes" v-model="orderNotes" placeholder="选填，可填写您的特殊需求"></textarea>
      </div>
      
      <div class="form-actions">
        <button class="btn btn-secondary" @click="$emit('close')">取消</button>
        <button 
          class="btn btn-primary" 
          @click="createOrder" 
          :disabled="!selectedAddress || submitting"
        >
          {{ submitting ? '提交中...' : '确认订单' }}
        </button>
      </div>
    </div>
    
    <!-- 步骤2: 支付 -->
    <div v-if="currentStep === 2" class="step-content">
      <div class="payment-info">
        <p>订单号：#{{ createdOrderId }}</p>
        <p class="total-amount">支付金额：¥{{ createdOrderAmount.toFixed(2) }}</p>
      </div>
      <div class="payment-methods">
        <div class="method-item" :class="{ active: paymentMethod === 'creditcard' }" @click="paymentMethod = 'creditcard'">
          <input type="radio" id="creditcard" value="creditcard" v-model="paymentMethod">
          <label for="creditcard">
            <img src="/images/creditcard.png" alt="信用卡" class="method-icon">
            <span class="method-copy">
              <strong>信用卡支付</strong>
              <span>适合快速完成当前订单支付</span>
            </span>
          </label>
        </div>
        <div class="method-item" :class="{ active: paymentMethod === 'bankcard' }" @click="paymentMethod = 'bankcard'">
          <input type="radio" id="bankcard" value="bankcard" v-model="paymentMethod">
          <label for="bankcard">
            <img src="/images/bankcard.png" alt="银行卡" class="method-icon">
            <span class="method-copy">
              <strong>银行卡支付</strong>
              <span>适合借记卡和储蓄卡快捷付款</span>
            </span>
          </label>
        </div>
        <div class="method-item" :class="{ active: paymentMethod === 'alipay' }" @click="paymentMethod = 'alipay'">
          <input type="radio" id="alipay" value="alipay" v-model="paymentMethod">
          <label for="alipay">
            <img src="/images/alipay.png" alt="支付宝" class="method-icon">
            <span class="method-copy">
              <strong>支付宝支付</strong>
              <span>适合日常移动端扫码与钱包付款</span>
            </span>
          </label>
        </div>
        <div class="method-item" :class="{ active: paymentMethod === 'wechat' }" @click="paymentMethod = 'wechat'">
          <input type="radio" id="wechat" value="wechat" v-model="paymentMethod">
          <label for="wechat">
            <img src="/images/wechat.png" alt="微信支付" class="method-icon">
            <span class="method-copy">
              <strong>微信支付</strong>
              <span>适合移动端快速确认并完成支付</span>
            </span>
          </label>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-secondary" @click="goToStep(1)">上一步</button>
        <button class="btn btn-primary" @click="payOrder" :disabled="paying">
          {{ paying ? '支付中...' : '立即支付' }}
        </button>
      </div>
    </div>
    
    <!-- 步骤3: 完成 -->
    <div v-if="currentStep === 3" class="step-content success-step">
      <div class="success-icon" aria-hidden="true"></div>
      <h3>支付成功</h3>
      <p>订单 #{{ createdOrderId }} 支付成功</p>
      <div class="form-actions">
        <button class="btn btn-secondary" @click="viewOrder">查看订单</button>
        <button class="btn btn-primary" @click="continueShopping">继续购物</button>
      </div>
    </div>

    <!-- 地址选择器弹窗 -->
    <div v-if="showAddressSelector" class="modal-overlay" @click.self="showAddressSelector = false">
      <div class="address-modal">
        <div class="modal-header">
          <h3>选择收货地址</h3>
          <button class="close-btn" @click="showAddressSelector = false">×</button>
        </div>
        
        <div v-if="loadingAddresses" class="loading-container small">
          <div class="spinner"></div>
          <p>加载中...</p>
        </div>
        <div v-else-if="addresses.length === 0" class="no-address">
          <p>暂无收货地址</p>
          <router-link to="/addresses" class="btn" @click="showAddressSelector = false; $emit('close')">去添加地址</router-link>
        </div>
        <div v-else class="address-list">
          <div 
            v-for="address in addresses" 
            :key="address.id" 
            class="address-option"
            :class="{ 'selected': tempSelectedAddressId === address.id }"
            @click="tempSelectedAddressId = address.id"
          >
            <div class="address-radio">
              <input type="radio" :id="'addr-' + address.id" :value="address.id" v-model="tempSelectedAddressId">
              <label :for="'addr-' + address.id"></label>
            </div>
            <div class="address-content">
              <div class="address-header">
                <span class="receiver-name">{{ address.receiverName }}</span>
                <span class="phone">{{ address.phone }}</span>
                <span v-if="address.isDefault" class="default-tag">默认</span>
              </div>
              <p class="address-detail">
                {{ address.province }} {{ address.city }} {{ address.district }} {{ address.detailAddress }}
              </p>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAddressSelector = false">取消</button>
          <button class="btn btn-primary" @click="confirmAddressSelection" :disabled="!tempSelectedAddressId">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CheckoutForm',
  props: {
    items: {
      type: Array,
      required: true
    },
    showSteps: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentStep: 1,
      addresses: [],
      selectedAddress: null,
      tempSelectedAddressId: null,
      loadingAddresses: false,
      orderNotes: '',
      submitting: false,
      paying: false,
      createdOrderId: null,
      createdOrderAmount: 0,
      showAddressSelector: false,
      paymentMethod: 'bankcard'
    };
  },
  computed: {
    orderItems() {
      return this.items;
    },
    productAmount() {
      return this.orderItems.reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0);
    },
    sizeExtraAmount() {
      return this.orderItems.reduce((total, item) => {
        return total + ((item.sizeExtraPrice || 0) * item.quantity);
      }, 0);
    },
    totalAmount() {
      return this.orderItems.reduce((total, item) => {
        return total + ((item.price + (item.sizeExtraPrice || 0)) * item.quantity);
      }, 0);
    }
  },
  created() {
    this.fetchAddresses();
  },
  methods: {
    async fetchAddresses() {
      this.loadingAddresses = true;
      try {
        const username = localStorage.getItem('customerUsername');
        const response = await axios.get('/addresses', {
          headers: { 'X-Username': username }
        });
        
        if (response.data.success) {
          this.addresses = response.data.addresses || [];
          const defaultAddress = this.addresses.find(addr => addr.isDefault);
          this.selectedAddress = defaultAddress || (this.addresses.length > 0 ? this.addresses[0] : null);
        }
      } catch (error) {
        console.error('获取地址失败:', error);
      } finally {
        this.loadingAddresses = false;
      }
    },
    
    confirmAddressSelection() {
      const address = this.addresses.find(addr => addr.id === this.tempSelectedAddressId);
      if (address) {
        this.selectedAddress = address;
      }
      this.showAddressSelector = false;
    },
    
    async createOrder() {
      if (!this.selectedAddress) {
        alert('请选择收货地址');
        return;
      }
      
      this.submitting = true;
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        
        const orderItems = this.orderItems.map(item => ({
          productId: item.productId || item.id,
          quantity: item.quantity,
          price: item.price,
          productName: item.productName,
          sizeDetailId: item.sizeDetailId || null,
          sizeName: item.sizeName || null,
          sizeExtraPrice: item.sizeExtraPrice || 0
        }));
        
        const orderRequest = {
          items: orderItems,
          buyerName: this.selectedAddress.receiverName,
          phone: this.selectedAddress.phone,
          address: this.selectedAddress.province + this.selectedAddress.city + this.selectedAddress.district + this.selectedAddress.detailAddress,
          notes: this.orderNotes
        };
        
        const response = await axios.post('/customer-orders/create', orderRequest, {
          headers: { 'X-Username': encodedUsername }
        });
        
        if (response.data.success) {
          this.createdOrderId = response.data.orderId;
          this.createdOrderAmount = response.data.totalAmount || this.totalAmount;
          this.currentStep = 2;
          this.$emit('order-created', this.createdOrderId);
        } else {
          alert('创建订单失败: ' + response.data.message);
        }
      } catch (error) {
        console.error('创建订单失败:', error);
        alert('创建订单失败: ' + (error.response?.data?.message || '请稍后重试'));
      } finally {
        this.submitting = false;
      }
    },
    
    async payOrder() {
      this.paying = true;
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        
        await axios.post(`/customer-orders/${this.createdOrderId}/pay`, {}, {
          headers: { 'X-Username': encodedUsername }
        });
        this.currentStep = 3;
        this.$emit('payment-success');
      } catch (error) {
        console.error('支付失败:', error);
        alert('支付失败: ' + (error.response?.data?.message || '请稍后重试'));
      } finally {
        this.paying = false;
      }
    },
    
    goToStep(step) {
      if (step < this.currentStep) {
        this.currentStep = step;
      }
    },
    
    viewOrder() {
      this.$router.push('/orders');
      this.$emit('close');
    },
    
    continueShopping() {
      this.$router.push('/');
      this.$emit('close');
    },
    
    getProductImageUrl(item) {
      if (!item.imageUrl) {
        return `https://picsum.photos/seed/product-${item.productId || item.id}/180/180.jpg`;
      }
      if (item.imageUrl.startsWith('http')) {
        return item.imageUrl;
      }
      return `/images/${item.imageUrl}`;
    }
  }
};
</script>

<style scoped>
.checkout-form {
  width: 100%;
}

/* 步骤进度条 */
.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  padding: 20px 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
}

.step.clickable {
  cursor: pointer;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: all 0.3s ease;
}

.step.active .step-circle {
  background-color: #ff4400;
  color: white;
}

.step.completed .step-circle {
  background-color: #4caf50;
  color: white;
}

.step.completed .step-circle::before {
  content: '';
  width: 11px;
  height: 6px;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg);
}

.step-label {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

.step.active .step-label {
  color: #ff4400;
  font-weight: 600;
}

.step-line {
  width: 80px;
  height: 2px;
  background-color: #e0e0e0;
  margin: 0 10px;
  margin-bottom: 25px;
}

.step-line.active {
  background-color: #4caf50;
}

/* 步骤内容 */
.step-content {
  padding: 20px 0;
}

/* 地址部分 */
.address-section {
  margin-bottom: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.section-title h3 {
  margin: 0;
  font-size: 16px;
  flex: 1;
}

.title-icon {
  position: relative;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 11px;
  font-size: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(241, 245, 249, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 10px 18px rgba(15, 23, 42, 0.08);
}

.title-icon::before,
.title-icon::after {
  content: '';
  position: absolute;
}

.title-icon::before {
  top: 8px;
  left: 8px;
  width: 14px;
  height: 12px;
  border: 2px solid #111827;
  border-radius: 4px;
}

.title-icon::after {
  top: 13px;
  left: 8px;
  width: 14px;
  height: 2px;
  background: #111827;
}

.address-card {
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.address-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.receiver-name {
  font-weight: bold;
  font-size: 15px;
}

.phone {
  color: #666;
}

.default-tag {
  background-color: #ff4400;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.address-detail {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.no-address {
  text-align: center;
  padding: 24px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.no-address p {
  color: #666;
  margin-bottom: 12px;
}

/* 商品部分 */
.products-section {
  margin-bottom: 24px;
}

.order-products {
  max-height: 300px;
  overflow-y: auto;
}

.order-product {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.order-product:last-child {
  border-bottom: none;
}

.product-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.product-info {
  flex: 1;
}

.product-name {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
}

.product-size {
  margin: 0 0 4px 0;
  font-size: 12px;
  color: #666;
}

.product-price {
  margin: 0;
  font-size: 14px;
  color: #ff4400;
  font-weight: 600;
}

.product-qty {
  font-size: 14px;
  color: #666;
}

.product-total {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  min-width: 80px;
  text-align: right;
}

/* 金额部分 */
.amount-section {
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.amount-row.total {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
  font-size: 16px;
  font-weight: 600;
}

/* 备注部分 */
.notes-section {
  margin-bottom: 24px;
}

.notes-section label {
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.notes-section textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

/* 支付方式 */
.payment-methods {
  margin-bottom: 24px;
  display: grid;
  gap: 0.85rem;
}

.method-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 0;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #fff;
}

.method-item:hover,
.method-item.active {
  border-color: #d1d5db;
  background-color: #f8fafc;
}

.method-item input[type="radio"] {
  margin-right: 12px;
}

.method-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex: 1;
}

.method-copy {
  display: grid;
  gap: 0.2rem;
}

.method-copy strong {
  color: #111827;
  font-size: 0.96rem;
}

.method-copy span {
  color: #6b7280;
  font-size: 0.86rem;
}

.method-icon {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.method-icon {
  font-size: 20px;
}

.payment-info {
  text-align: center;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 12px;
}

.payment-info p {
  margin: 8px 0;
  color: #666;
}

.total-amount {
  font-size: 24px;
  font-weight: bold;
  color: #1d1d1f !important;
}

/* 成功步骤 */
.success-step {
  text-align: center;
  padding: 40px 20px;
}

.success-icon {
  width: 80px;
  height: 80px;
  background-color: #4caf50;
  border-radius: 50%;
  position: relative;
  margin: 0 auto 20px;
}

.success-icon::before {
  content: '';
  position: absolute;
  top: 28px;
  left: 24px;
  width: 24px;
  height: 12px;
  border-left: 4px solid #fff;
  border-bottom: 4px solid #fff;
  transform: rotate(-45deg);
}

.success-step h3 {
  margin-bottom: 12px;
}

.success-step p {
  color: #666;
  margin-bottom: 24px;
}

/* 表单操作按钮 */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #1d1d1f;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2c2c2e;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #666;
}

.btn-secondary:hover {
  background-color: #e8e8e8;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #ddd;
  color: #666;
}

.btn-outline:hover {
  border-color: #d1d5db;
  color: #111827;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.address-modal {
  background-color: white;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
}

.address-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.address-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.address-option:hover,
.address-option.selected {
  border-color: #ff4400;
  background-color: #fff5f5;
}

.address-radio {
  margin-top: 2px;
}

.address-content {
  flex: 1;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  justify-content: flex-end;
}

.loading-container {
  text-align: center;
  padding: 40px;
}

.loading-container.small {
  padding: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #ff4400;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
